/**
 * MachineScheduleGrid.tsx
 * Day-granularity grid for the Machine Schedule view.
 *
 * Layout:
 *   - Sticky left column: machine code + name, grouped by LabSection headers
 *   - Scrollable right area: day columns × machine rows
 *   - Booking bars rendered position:absolute inside each row's relative day-span
 *
 * Machine Schedule UX overhaul:
 *   - M2: grid-level multi-mode drag (resize both edges, move horizontally, move
 *         across capable machine rows). The grid owns useBookingDrag and renders
 *         a floating <DragGhost> in the target row + a <DragDateTooltip>.
 *   - Single-click (no drag) → onBookingClick (detail drawer, M3).
 *
 * Color semantics: all tokens from theme.schedule.* (no hex literals).
 */

import React, { useMemo, useRef, useLayoutEffect, useState, useEffect, useCallback } from 'react';
import { Box, Typography, Chip, Collapse } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { PrecisionManufacturing, ExpandMore } from '@mui/icons-material';
import type { MachineBookingCalendar } from '../../../types/schedule';
import type { MachineScheduleGridProps } from './machineScheduleGridProps';
import { groupMachineRowsTwoLevel, filterIndexStationsForSchedule, topSectionKey } from './machineScheduleRowGrouping';
import { MachineRowLabelCell } from './MachineRowLabelCell';
import type { ConflictType } from '../../../components/schedule/ConflictBadge';
import { DragDateTooltip } from '../../../components/schedule/DragGhost';
import { NowLine } from '../../../components/schedule/NowLine';
import { useBookingDrag } from '../../../hooks/useBookingDrag';
import { useCreateDrag } from '../../../hooks/useCreateDrag';
import { MachineScheduleHeader } from './MachineScheduleHeader';
import { GridScrollArrows } from '../../../components/tables/GridScrollArrows';
import { useMachineGridChrome } from './useMachineGridChrome';
import { useCollapsedSections } from './useCollapsedSections';
import { MachineScheduleBookingLane } from './MachineScheduleBookingLane';
import { MachineScheduleIndexTestRows } from './MachineScheduleIndexTestRows';
import { isIndexMachineCode } from '../../../utils/indexMachine';
import {
  LEFT_COL_WIDTH,
  ROW_HEIGHT,
  DAY_COL_MIN,
  buildDayRange,
} from './scheduleGridShared';
import { useScheduleHolidays } from '../../../hooks/useScheduleHolidays';
import { mergePrepBookings, weekdayOnly } from '../../../utils/prepBookingMerge';

// ── component ──────────────────────────────────────────────────────────────

export const MachineScheduleGrid: React.FC<MachineScheduleGridProps> = ({
  bookings: rawBookings,
  allMachineRows,
  showIndexTestRows = false,
  startDate,
  endDate,
  onCommitPlacement,
  onBookingClick,
  onInfo,
  onDragActiveChange,
  capabilityByTaskType,
  forceExpandAll = false,
  showCapacity = false,
  dayColWidth = DAY_COL_MIN,
  onCreateAt,
  selectedKey,
  conflicts,
  ghostBookings: rawGhostBookings,
  fillHeight = false,
  isBookingReadOnly,
  rowHeight = ROW_HEIGHT,
  compactLabels = false,
  pageFlowStickyTop,
  onDayColWidthDelta,
  onPageBeyondEdge,
}) => {
  const theme = useTheme();

  // ── measure day-column width for drag math ──────────────────────────────
  const daySpanRef = useRef<HTMLDivElement | null>(null);
  const [dayWidthPx, setDayWidthPx] = useState<number>(dayColWidth);

  const days = useMemo(() => buildDayRange(startDate, endDate), [startDate, endDate]);
  const totalDays = days.length;

  // ── stat-holiday shading ─────────────────────────────────────────────────
  // The lab is closed on stat holidays, so those columns get a (distinct) non-
  // work tint alongside weekends. holidaysInRange comes from the server (region
  // defaults to NB); nameByDate drives the per-cell tooltip hint.
  const { nameByDate: holidayNameByDate } = useScheduleHolidays(startDate, endDate);

  // BUG-2026-0193: a source's per-line rock-prep bookings on one machine
  // (Cutting × N on the saw, …) render as ONE bar — they were minute-packed onto
  // the same day and drawn on top of each other. Display-only fold (the rows
  // stay per line); a drag on the merged bar fans out to every member via the
  // caller's onCommitPlacement (utils/prepBookingMerge.expandPrepPatch).
  const isWorkingDay = useCallback(
    (iso: string) => weekdayOnly(iso) && !holidayNameByDate.has(iso),
    [holidayNameByDate],
  );
  const bookings = useMemo(() => mergePrepBookings(rawBookings, isWorkingDay), [rawBookings, isWorkingDay]);
  const ghostBookings = useMemo(
    () => (rawGhostBookings ? mergePrepBookings(rawGhostBookings, isWorkingDay) : undefined),
    [rawGhostBookings, isWorkingDay],
  );

  useLayoutEffect(() => {
    const el = daySpanRef.current;
    if (!el || totalDays === 0) return;
    const updateWidth = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) setDayWidthPx(w / totalDays);
    };
    updateWidth();
    const ro = new ResizeObserver(updateWidth);
    ro.observe(el);
    return () => ro.disconnect();
  }, [totalDays]);

  // ── grid chrome: page-flow sticky shim, ◀/▶ paging arrows, Ctrl+wheel zoom ─
  const { rootRef, headerWrapRef, arrowsRef, canScrollLeft, canScrollRight, pageBy } =
    useMachineGridChrome({
      pageFlowStickyTop, totalDays, dayColWidth, rowHeight, onDayColWidthDelta, onPageBeyondEdge,
    });

  // ── booking + machine maps ────────────────────────────────────────────────
  const bookingsByMachine = useMemo(() => {
    const map = new Map<string, MachineBookingCalendar[]>();
    for (const b of bookings) {
      if (!map.has(b.MachineKey)) map.set(b.MachineKey, []);
      map.get(b.MachineKey)!.push(b);
    }
    return map;
  }, [bookings]);

  // Ghost (baseline) bookings by machine — FEAT-2026-0007 6B.8.
  const ghostBookingsByMachine = useMemo(() => {
    const map = new Map<string, MachineBookingCalendar[]>();
    for (const b of ghostBookings ?? []) {
      if (!map.has(b.MachineKey)) map.set(b.MachineKey, []);
      map.get(b.MachineKey)!.push(b);
    }
    return map;
  }, [ghostBookings]);

  // Two-level model (migration 120): top categories (Soil → Rock → Index) →
  // test-family groups → rows, incl. synthesized flagged rows for deactivated
  // machines with bookings (G2-10). Pure builder in machineScheduleRowGrouping.ts.
  const topSections = useMemo(() => {
    const rows = showIndexTestRows
      ? filterIndexStationsForSchedule(allMachineRows, bookings)
      : allMachineRows.filter((m) => !isIndexMachineCode(m.code));
    const sections = groupMachineRowsTwoLevel(rows, bookings);
    // Per-test INDEX category (2026-08-02): always present when enabled, even
    // with zero station machines left — the test lanes render inside it.
    if (showIndexTestRows && !sections.some((t) => t.top === 'Index')) {
      sections.push({ top: 'Index', key: topSectionKey('Index'), groups: [], machineCount: 0 });
    }
    return sections;
  }, [allMachineRows, bookings, showIndexTestRows]);

  // FEAT-2026-0014 — persisted collapse + one-click Expand/Collapse-all, now
  // over BOTH levels (top keys `top:X`, group keys `X/Y` — stable strings the
  // hook persists as before), plus a per-group booking count so a collapsed
  // group still tells you how much is on it.
  const sectionNames = useMemo(
    () => topSections.flatMap((t) => [t.key, ...t.groups.map((g) => g.key)]),
    [topSections],
  );
  const { collapsedSections, toggleSection, allCollapsed, setAllCollapsed } =
    useCollapsedSections(sectionNames, forceExpandAll);
  const bookingCountBySection = useMemo(() => {
    const counts = new Map<string, number>();
    for (const t of topSections) {
      let topCount = 0;
      for (const g of t.groups) {
        let c = 0;
        for (const machine of g.machines) {
          c += bookings.filter((b) => b.MachineKey === machine.machineKey).length;
        }
        counts.set(g.key, c);
        topCount += c;
      }
      counts.set(t.key, topCount);
    }
    return counts;
  }, [topSections, bookings]);

  const conflictTypeMap = useMemo<Map<string, ConflictType>>(() => {
    const map = new Map<string, ConflictType>();
    if (!conflicts) return map;
    for (const sc of conflicts.soft) map.set(sc.bookingKey, 'soft');
    for (const hc of conflicts.hard) for (const b of hc.bookings) map.set(b.key, 'hard');
    return map;
  }, [conflicts]);

  // machineKey → display code (for drag labels + tooltip).
  const machineCodeByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of allMachineRows) map.set(m.machineKey, m.code);
    for (const b of bookings) if (!map.has(b.MachineKey)) map.set(b.MachineKey, b.MachineCode);
    return map;
  }, [allMachineRows, bookings]);

  const getMachineCode = useCallback(
    (key: string) => machineCodeByKey.get(key) ?? key.slice(0, 4),
    [machineCodeByKey],
  );

  // Capable machine set for a booking (null = any machine allowed).
  const getCapableMachineKeys = useCallback(
    (booking: MachineBookingCalendar): Set<string> | null => {
      if (!booking.TaskTypeKey) return null; // manual events: no constraint
      const set = capabilityByTaskType.get(booking.TaskTypeKey);
      return set && set.size > 0 ? set : null;
    },
    [capabilityByTaskType],
  );

  // ── drag engine (grid-level) ────────────────────────────────────────────────
  const { activeDrag, beginDrag } = useBookingDrag({
    dayWidthPx,
    getCapableMachineKeys,
    getMachineCode,
    onCommit: onCommitPlacement,
    onClick: onBookingClick,
    onInvalidDrop: onInfo,
  });

  // Report drag-active up (for keyboard undo / auto-refresh gating).
  useEffect(() => {
    onDragActiveChange?.(activeDrag !== null);
  }, [activeDrag, onDragActiveChange]);

  const showGhostFor = (machineKey: string): boolean =>
    activeDrag !== null &&
    activeDrag.ghostMachineKey === machineKey &&
    (activeDrag.moved || activeDrag.mode !== 'move');

  // ── now-line position + column cross-highlight ───────────────────────────
  const [hoverCol, setHoverCol] = useState<number | null>(null);
  const todayStr = new Date().toISOString().slice(0, 10);
  const todayIdx = days.indexOf(todayStr);
  const nowLeftPx =
    todayIdx < 0 || totalDays === 0
      ? null
      : (() => {
          const now = new Date();
          const frac = (todayIdx + (now.getHours() * 60 + now.getMinutes()) / 1440) / totalDays;
          return LEFT_COL_WIDTH + frac * totalDays * dayWidthPx;
        })();
  const colHighlight = alpha(theme.palette.primary.main, 0.07);

  // ── drag across empty cells to create an event ───────────────────────────
  const { createSel, beginCreate } = useCreateDrag(days, totalDays, onCreateAt);

  const borderColor = theme.border.color;
  // Non-work columns are now marked by the IN-FRONT solid veil
  // (MachineScheduleNonWorkOverlay), which covers EMPTY cells too. The old
  // behind-bar tint is therefore redundant and only double-darkened empty closed
  // cells (veil + tint stacked) so they read heavier than cells under a bar. Drop
  // it so every closed column looks the same (UX audit 2026-06-20).
  const weekendBg = 'transparent';
  const holidayBg = 'transparent';
  // Today gets a blue accent tint (matching the now-line) so it reads clearly
  // different from the neutral-grey weekend columns — see BUG-2026-0096.
  const todayBg = alpha(theme.schedule.todayColumn, 0.14);
  const todayBorder = `2px solid ${alpha(theme.schedule.todayColumn, 0.55)}`;
  // Tint tokens for the day-background layer (extracted to MachineScheduleDayCells).
  const dayCellColors = {
    borderColor: alpha(borderColor, 0.35), todayBorder, colHighlight, todayBg, holidayBg, weekendBg,
  };
  // Solid-veil colors for the IN-FRONT non-work overlay (MachineScheduleNonWorkOverlay).
  // A solid translucent veil (not a hatch) so it can't be confused with the sage
  // hatch the tentative bars use — amber = holiday (the app's "closed" hue), neutral
  // grey = weekend. 2026-07 sticky-headers UX pass: lightened substantially (0.20 →
  // 0.08 / 0.34 → 0.16) — the heavy veil made empty weekend columns read as solid
  // grey blocks and drowned bar labels; a light tint still notches bars on closed
  // days while keeping the grid airy.
  const nonWorkColors = {
    holiday: alpha(theme.schedule.maintenance, 0.16),
    weekend: alpha(theme.palette.text.primary, 0.08),
    // Run Mode Phase C: weekend/holiday cells under a CONTINUOUS bar tint in
    // the continuous token, faint enough that the bar underneath stays the
    // star — "running through the weekend", not "lab closed".
    continuousRun: alpha(theme.schedule.continuous, 0.14),
  };

  if (allMachineRows.length === 0 && bookings.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
        <PrecisionManufacturing sx={{ fontSize: 48, color: theme.schedule.machineTime, opacity: 0.3 }} />
        <Typography color="text.secondary" variant="body2">
          No machines found. Add machines in the Machines page.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={rootRef}
      sx={{
        overflow: 'auto',
        border: `1px solid ${borderColor}`,
        borderRadius: 1,
        backgroundColor: theme.palette.background.paper,
        // FEAT-2026-0013 — when the page bounds our height, become the single
        // scroll container so the sticky header (top:0) + sticky left column
        // freeze on scroll. minHeight:0 lets a flex child actually shrink so
        // the inner content scrolls instead of stretching the parent.
        ...(fillHeight ? { flex: 1, minHeight: 0 } : {}),
      }}
    >
      {/* ◀/▶ paging arrows — zero-height sticky overlay (top:50% centres it in
          the bounded fillHeight mode; the page-flow shim positions it via
          transform). Rendered before the header so it never affects layout. */}
      {(canScrollLeft || canScrollRight) && (
        <GridScrollArrows
          ref={arrowsRef}
          canScrollLeft={canScrollLeft}
          canScrollRight={canScrollRight}
          onScroll={pageBy}
          leftInset={LEFT_COL_WIDTH}
          // Page-flow mode: the shim owns vertical placement — the built-in
          // sticky top:50% would stack with the shim's translate and push the
          // arrows to the grid's bottom edge (2026-07-24 round 4).
          disableSticky={pageFlowStickyTop != null}
        />
      )}

      {/* ── Header row ──────────────────────────────────────── */}
      {pageFlowStickyTop != null ? (
        // Page-flow mode: the scroll-sync shim translates this wrapper; it must
        // ride ABOVE the sticky left labels (2), group headers (3), the non-work
        // veil (4) and the header's own zIndex (5). Do NOT wrap in contained
        // mode — an intermediate box would become the sticky containing block
        // and kill the native sticky there.
        <Box ref={headerWrapRef} sx={{ position: 'relative', zIndex: 7, willChange: 'transform' }}>
          <MachineScheduleHeader
            days={days}
            totalDays={totalDays}
            hoverCol={hoverCol}
            dayColWidth={dayColWidth}
          />
        </Box>
      ) : (
        <MachineScheduleHeader
          days={days}
          totalDays={totalDays}
          hoverCol={hoverCol}
          dayColWidth={dayColWidth}
        />
      )}

      {/* FEAT-2026-0014 — expand/collapse every lab-section (test-type) group at
          once, so a cluttered schedule collapses to an overview in one click. */}
      {sectionNames.length > 1 && (
        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            px: 1.5, py: 0.4,
            position: 'sticky', left: 0, zIndex: 2,
            width: 'fit-content', minWidth: LEFT_COL_WIDTH,
            borderBottom: `1px solid ${alpha(borderColor, 0.5)}`,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.62rem', fontWeight: 700 }}>
            {topSections.length} categories · {topSections.reduce((n, t) => n + t.groups.length, 0)} test groups
          </Typography>
          <Typography
            component="button"
            type="button"
            onClick={() => setAllCollapsed(!allCollapsed)}
            sx={{
              ml: 'auto', border: 0, background: 'none', p: 0, cursor: 'pointer',
              fontSize: '0.64rem', fontWeight: 700, color: theme.palette.primary.main, textDecoration: 'underline',
              '&:hover': { color: theme.palette.primary.dark ?? theme.palette.primary.main },
            }}
          >
            {allCollapsed ? 'Expand all' : 'Collapse all'}
          </Typography>
        </Box>
      )}

      {/* ── Machine rows: TOP category (Soil/Rock/Index) → test group ──────── */}
      <Box sx={{ position: 'relative' }} onMouseLeave={() => setHoverCol(null)}>
      {topSections.map((topSection) => {
        const isTopCollapsed = !forceExpandAll && collapsedSections.has(topSection.key);
        return (
        <React.Fragment key={topSection.key}>
          {/* TOP category header — heavier band, click to collapse the whole category. */}
          <Box
            onClick={() => toggleSection(topSection.key)}
            sx={{
              display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none',
              backgroundColor: alpha(theme.palette.primary.main, 0.16),
              borderBottom: `1px solid ${borderColor}`,
              minWidth: LEFT_COL_WIDTH + totalDays * dayColWidth,
              position: 'sticky', zIndex: 3,
              '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.22) },
            }}
          >
            <Box
              sx={{
                width: LEFT_COL_WIDTH, minWidth: LEFT_COL_WIDTH, px: 1.5, py: 0.6,
                borderRight: `1px solid ${borderColor}`,
                display: 'flex', alignItems: 'center', gap: 0.75,
                position: 'sticky', left: 0, zIndex: 4,
                backgroundColor: theme.palette.background.paper,
                backgroundImage: `linear-gradient(${alpha(theme.palette.primary.main, 0.16)}, ${alpha(theme.palette.primary.main, 0.16)})`,
              }}
            >
              <ExpandMore
                sx={{
                  fontSize: 20, color: theme.palette.primary.dark ?? theme.palette.primary.main,
                  transition: 'transform 0.2s ease',
                  transform: isTopCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                }}
              />
              <Typography sx={{
                fontSize: '0.74rem', fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase',
                color: theme.palette.primary.dark ?? theme.palette.primary.main,
              }}>
                {topSection.top}
              </Typography>
              <Typography variant="caption" sx={{ ml: 'auto', color: 'text.secondary', fontSize: '0.62rem', whiteSpace: 'nowrap' }}>
                {showIndexTestRows && topSection.top === 'Index'
                  ? 'scheduled by test'
                  : `${topSection.machineCount} ${topSection.machineCount === 1 ? 'machine' : 'machines'}${(bookingCountBySection.get(topSection.key) ?? 0) > 0 ? ` · ${bookingCountBySection.get(topSection.key)} booked` : ''}`}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, height: 30 }} />
          </Box>

          <Collapse in={!isTopCollapsed} timeout="auto">
          {/* 2026-08-02 (Gigi/Mike): the INDEX category schedules BY TEST — one
              pooled-utilization lane per index test, no station rows. Any
              station that still carries a real booking follows below. */}
          {showIndexTestRows && topSection.top === 'Index' && (
            <MachineScheduleIndexTestRows
              days={days}
              dayColWidth={dayColWidth}
              rowHeight={rowHeight}
              labelWidth={LEFT_COL_WIDTH}
              compact={compactLabels}
            />
          )}
          {topSection.groups.map((groupSection) => {
            const section = groupSection.key;
            const machines = groupSection.machines;
            const isCollapsed = !forceExpandAll && collapsedSections.has(section);
            return (
            <React.Fragment key={section}>
          {/* Test-family group header row — click to collapse / expand */}
          <Box
            onClick={() => toggleSection(section)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              userSelect: 'none',
              backgroundColor: alpha(theme.palette.primary.main, 0.06),
              borderBottom: `1px solid ${borderColor}`,
              minWidth: LEFT_COL_WIDTH + totalDays * dayColWidth,
              position: 'sticky',
              zIndex: 3,
              '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) },
            }}
          >
            <Box
              sx={{
                width: LEFT_COL_WIDTH,
                minWidth: LEFT_COL_WIDTH,
                px: 1.5,
                py: 0.5,
                borderRight: `1px solid ${borderColor}`,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                // Freeze the group label with the machine column (the chip slid
                // away on horizontal scroll while row labels stayed). Needs an
                // OPAQUE ground under the row's translucent tint so day cells
                // don't show through while sliding underneath.
                position: 'sticky',
                left: 0,
                zIndex: 4, // above bars (2) AND the non-work veil (3) — see MachineRowLabelCell
                backgroundColor: theme.palette.background.paper,
                backgroundImage: `linear-gradient(${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.06)})`,
              }}
            >
              <ExpandMore
                sx={{
                  fontSize: 18,
                  color: theme.palette.text.secondary,
                  transition: 'transform 0.2s ease',
                  transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                }}
              />
              <Chip
                label={groupSection.name}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  backgroundColor: alpha(theme.palette.primary.main, 0.15),
                  color: theme.palette.primary.dark ?? theme.palette.primary.main,
                }}
              />
              <Typography
                variant="caption"
                sx={{ ml: 'auto', color: 'text.secondary', fontSize: '0.62rem', whiteSpace: 'nowrap' }}
              >
                {machines.length} {machines.length === 1 ? 'machine' : 'machines'}
                {(bookingCountBySection.get(section) ?? 0) > 0 ? ` · ${bookingCountBySection.get(section)} booked` : ''}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, height: 26 }} />
          </Box>

          {/* Individual machine rows (collapsible, smooth in/out) */}
          <Collapse in={!isCollapsed} timeout="auto">
          {machines.map((machine) => {
            const machineBookings = bookingsByMachine.get(machine.machineKey) ?? [];
            return (
              <Box
                key={machine.machineKey}
                data-machine-key={machine.machineKey}
                sx={{
                  display: 'flex',
                  height: rowHeight,
                  borderBottom: `1px solid ${alpha(borderColor, 0.5)}`,
                  minWidth: LEFT_COL_WIDTH + totalDays * dayColWidth,
                  '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.02) },
                }}
              >
                {/* Machine name cell (incl. G2-10 deactivated flag chip). Capacity
                    strip is minutes-based (A4) — never meaningful for the Index row. */}
                <MachineRowLabelCell
                  machine={machine}
                  machineBookings={machineBookings}
                  days={days}
                  showCapacity={showCapacity}
                  compact={compactLabels}
                />

                {/* Day-span area: booking bars + drag ghost. (The old single
                    INDEX pooled lane is gone — the Index category renders
                    per-test lanes above via MachineScheduleIndexTestRows.) */}
                <Box
                  ref={daySpanRef}
                  // Fixed lane width = Σ day cells, so the bars' fractional
                  // positions stay aligned with the (now fixed-width) cells.
                  sx={{ width: totalDays * dayColWidth, flexShrink: 0, position: 'relative', display: 'flex' }}
                  onMouseMove={(e) => {
                    const r = e.currentTarget.getBoundingClientRect();
                    const idx = Math.floor(((e.clientX - r.left) / r.width) * totalDays);
                    setHoverCol((p) => (idx >= 0 && idx < totalDays && idx !== p ? idx : p));
                  }}
                  onPointerDown={(e) => beginCreate(e, machine.machineKey)}
                >
                  {(
                    <MachineScheduleBookingLane
                      machineKey={machine.machineKey}
                      machineBookings={machineBookings}
                      ghostBookings={ghostBookingsByMachine.get(machine.machineKey)}
                      days={days}
                      hoverCol={hoverCol}
                      dayColWidth={dayColWidth}
                      holidayNameByDate={holidayNameByDate}
                      dayCellColors={dayCellColors}
                      nonWorkColors={nonWorkColors}
                      startDate={startDate}
                      endDate={endDate}
                      totalDays={totalDays}
                      dayWidthPx={dayWidthPx}
                      conflictTypeMap={conflictTypeMap}
                      selectedKey={selectedKey}
                      isBookingReadOnly={isBookingReadOnly}
                      activeDrag={activeDrag}
                      showGhost={showGhostFor(machine.machineKey)}
                      onBeginDrag={beginDrag}
                      createSel={createSel}
                    />
                  )}
                </Box>
              </Box>
            );
          })}
          </Collapse>
            </React.Fragment>
            );
          })}
          </Collapse>
        </React.Fragment>
        );
      })}
      {nowLeftPx !== null && <NowLine leftPx={nowLeftPx} />}
      </Box>

      {/* Live drag date tooltip (follows the pointer) */}
      {activeDrag && (activeDrag.moved || activeDrag.mode !== 'move') && (
        <DragDateTooltip
          x={activeDrag.pointerX}
          y={activeDrag.pointerY}
          start={activeDrag.ghostStart}
          end={activeDrag.ghostEnd}
          machineCode={
            activeDrag.ghostMachineKey !== activeDrag.originalMachineKey
              ? getMachineCode(activeDrag.ghostMachineKey)
              : null
          }
          valid={activeDrag.valid}
        />
      )}
    </Box>
  );
};
